import { startOfToday, subDays, format } from 'date-fns';
import { AuthenticatedHttp } from '../../http/axios';
import { parsePaginationResponse } from '../../utils/api';
import { downloadFile } from '../../utils/files';

function getDateRangeFromPeriod(period) {
  const today = startOfToday();

  switch (period) {
    case 'today':
      return {
        startDate: format(today, 'dd-MM-yyyy'),
        endDate: format(today, 'dd-MM-yyyy'),
      };
    case '7days':
      return {
        startDate: format(subDays(today, 6), 'dd-MM-yyyy'),
        endDate: format(today, 'dd-MM-yyyy'),
      };
    case '30days':
      return {
        startDate: format(subDays(today, 29), 'dd-MM-yyyy'),
        endDate: format(today, 'dd-MM-yyyy'),
      };
    default:
      return {};
  }
}

export const getOrders = async ({ page, search, status, filter }) =>
  AuthenticatedHttp.get('/orders/', {
    params: {
      page,
      search,
      status,
      ...(filter.period !== 'all_time' && getDateRangeFromPeriod(filter.period)),
      ...(filter.startDate &&
        filter.period !== 'all_time' && {
        startDate: format(filter.startDate, 'dd-MM-yyyy'),
      }),
      ...(filter.endDate &&
        filter.period !== 'all_time' && {
        endDate: format(filter.endDate, 'dd-MM-yyyy'),
      }),
      ...(filter.coproductionType !== 'all' && {
        coproductionType: filter.coproductionType,
      }),
      ...(filter.products.length > 0 && {
        products: filter.products,
      }),
      ...(filter?.offers?.length > 0 && {
        offers: filter.offers,
      }),
      ...(filter.affiliates.length > 0 && {
        affiliate: filter.affiliates,
      }),
      ...(filter.paymentMethods.length > 0 && {
        paymentMethods: filter.paymentMethods,
      }),
      ...(filter.status.length > 0 && {
        status: filter.status,
      }),
      ...(filter.subscription_type.length > 0 && {
        subscription_type: filter.subscription_type,
      }),
      ...(filter?.offerType !== 'all' && {
        offerType: filter.offerType,
      }),
      ...(!!filter?.utm_source && {
        utm_source: filter.utm_source,
      }),
      ...(!!filter?.utm_medium && {
        utm_medium: filter.utm_medium,
      }),
      ...(!!filter?.utm_campaign && {
        utm_campaign: filter.utm_campaign,
      }),
      ...(!!filter?.utm_content && {
        utm_content: filter.utm_content,
      }),
      ...(!!filter?.utm_term && {
        utm_term: filter.utm_term,
      }),
      ...(!!filter?.sck && {
        sck: filter.sck,
      }),
    },
  }).then(parsePaginationResponse);

export const refundOrderService = async ({ data }) =>
  AuthenticatedHttp.post(`/order/${data.id}/refund/`).then(({ data: order }) => order);

export const exportReport = async (values) => {
  const { extension, page, search, status, filter } = values;

  const queryParams = {
    page,
    search,
    status,
    ...(filter?.startDate && filter?.period !== 'all_time' && {
      startDate: format(filter.startDate, 'dd-MM-yyyy'),
    }),
    ...(filter?.endDate && filter?.period !== 'all_time' && {
      endDate: format(filter.endDate, 'dd-MM-yyyy'),
    }),
    ...(filter?.coproductionType && filter.coproductionType !== 'all' && {
      coproductionType: filter.coproductionType,
    }),
    ...(filter?.offerType && filter.offerType !== 'all' && {
      offerType: filter.offerType,
    }),
    ...(Array.isArray(filter?.products) && filter.products.length > 0 && {
      products: filter.products,
    }),
    ...(Array.isArray(filter?.offers) && filter.offers.length > 0 && {
      offers: filter.offers,
    }),
    ...(Array.isArray(filter?.affiliates) && filter.affiliates.length > 0 && {
      affiliate: filter.affiliates,
    }),
    ...(Array.isArray(filter?.paymentMethods) && filter.paymentMethods.length > 0 && {
      paymentMethods: filter.paymentMethods,
    }),
    ...(Array.isArray(filter?.status) && filter.status.length > 0 && {
      status: filter.status,
    }),
    ...(Array.isArray(filter?.shipping_status) && filter?.shipping_status?.length > 0 && {
      shipping_status: filter.shipping_status,
    }),
  };

  const res = await AuthenticatedHttp.post(
    '/reports/orders/export/email/',
    { format: extension },
    { params: queryParams, responseType: 'arraybuffer', validateStatus: (s) => s >= 200 && s < 400 }
  );

  const contentType = res.headers?.['content-type'] || '';
  if (contentType.includes('application/json')) {
    const json = JSON.parse(new TextDecoder().decode(res.data));

    if (json?.job_id) {
      return { emailed: true };
    }

    if (json?.redirect_to) {
      let url = json.redirect_to;
      if (/^\/api(\/|$)/.test(url)) url = url.replace(/^\/api/, '');
      const csvRes = await AuthenticatedHttp.get(url, { responseType: 'blob' });
      const blob = new Blob([csvRes.data]);
      const fileExt = url.includes('/csv') ? 'csv' : extension || 'csv';
      downloadFile({
        url: window.URL.createObjectURL(blob),
        filename: `orders_report.${fileExt}`,
      });
      return { emailed: false };
    }

    return { emailed: false };
  }

  const blob = new Blob([res.data]);
  downloadFile({
    url: window.URL.createObjectURL(blob),
    filename: `orders_report.${extension}`,
  });
  return { emailed: false };
};

export const resendOrderEmailService = async (orderId) =>
  AuthenticatedHttp.post(`/order/${orderId}/resend_approved_email/`).then(({ data }) => data);
